import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { X } from "@phosphor-icons/react"

import { cn } from "~/utils/helpers"

const filterPillVariants = cva(
  "rounded-full inline-flex items-center justify-center whitespace-nowrap transition focus-visible:outline-hidden disabled:cursor-not-allowed cursor-pointer",
  {
    variants: {
      variant: {
        primarySoft:
          "bg-component text-fg-base [&_svg]:fill-fg-subtle hover:bg-component-hover shadow-button-neutral",
        neutralOutline:
          "bg-transparent outline outline-1 outline-dashed outline-border-base text-fg-subtle [&_svg]:fill-fg-subtle hover:outline-solid hover:outline-border-strong dark:hover:from-zinc-800/90 dark:hover:to-zinc-950 focus-visible:shadow-button-neutral-focus focus:to-subtle active:bg-background-medium disabled:text-fg-disabled disabled:[&_svg]:fill-fg-disabled disabled:outline-border-base disabled:hover:outline-border-base disabled:hover:bg-button-transparent disabled:hover:shadow-none",
      },
      size: {
        "2xs": "h-5 px-1 py-0 label-base",
        xs: "h-6 px-1.5 py-0.5 label-base",
        sm: "h-7 px-2 py-1 label-base",
        base: "h-8 px-2.5 py-1.5 label-base",
      },
    },
    defaultVariants: {
      variant: "primarySoft",
      size: "sm",
    },
  },
)

interface FilterPillProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof filterPillVariants> {
  asChild?: boolean
  icon?: React.ReactNode
  onRemove?: (e: React.MouseEvent) => void
  showRemove?: boolean
}

const FilterPill = React.forwardRef<HTMLButtonElement, FilterPillProps>(
  (
    { className, variant, size, asChild = false, icon, onRemove, showRemove, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"

    const handleRemoveClick = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onRemove?.(e)
    }

    const isIconOnly = icon && !children

    const iconOnlyWidthClass = isIconOnly
      ? {
          "2xs": "w-5",
          xs: "w-6",
          sm: "w-7",
          base: "w-8",
        }[size || "sm"]
      : ""

    return (
      <Comp
        data-slot="filter-pill"
        className={cn(filterPillVariants({ variant, size, className }), iconOnlyWidthClass)}
        ref={ref}
        {...props}
      >
        <span className="flex items-center justify-center gap-1.5">
          {icon}
          {children}
          {showRemove && (
            <span
              onClick={handleRemoveClick}
              role="button"
              aria-label="Remove filter"
              className="flex items-center h-full"
            >
              <X size={16} weight="bold" />
            </span>
          )}
        </span>
      </Comp>
    )
  },
)

FilterPill.displayName = "FilterPill"

export { FilterPill, filterPillVariants }
